<template>
  <div class="file-upload-container">
    <label v-if="label" class="text-input-label">{{ label }}<span v-if="required" class="required-indicator">*</span></label>
    <div
      class="file-upload-area"
      :class="areaClass"
      @click="triggerFileInput"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        @change="handleFileSelect"
        class="file-input-hidden"
      />
      <template v-if="!modelValue">
        <vue-feather type="upload" size="16" class="upload-icon" />
        <span class="placeholder-text">{{ isDragOver ? 'Drop file here...' : placeholder }}</span>
      </template>
      <template v-else>
        <vue-feather type="file-text" size="16" class="file-icon" />
        <span class="file-name">{{ modelValue.name }}</span>
        <button class="remove-btn" @click.stop="removeFile" type="button">
          <vue-feather type="x" size="14" />
        </button>
      </template>
    </div>
    <span v-if="error" class="file-upload-error">{{ error }}</span>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'

export default {
  name: 'FileUploadInput',
  components: {
    'vue-feather': VueFeather
  },
  props: {
    modelValue: {
      type: [File, null],
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Choose a file or drag it here'
    },
    accept: {
      type: String,
      default: '*/*'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDragOver: false
    }
  },
  computed: {
    areaClass() {
      return {
        'file-upload-area--has-file': !!this.modelValue,
        'file-upload-area--drag-over': this.isDragOver,
        'file-upload-area--disabled': this.disabled,
        'file-upload-area--error': this.error
      }
    }
  },
  methods: {
    triggerFileInput() {
      if (this.disabled) return
      this.$refs.fileInput.click()
    },
    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        this.validateAndEmit(file)
      }
    },
    handleDragOver() {
      if (this.disabled) return
      this.isDragOver = true
    },
    handleDragLeave() {
      this.isDragOver = false
    },
    handleDrop(event) {
      this.isDragOver = false
      if (this.disabled) return
      const file = event.dataTransfer.files[0]
      if (file) {
        this.validateAndEmit(file)
      }
    },
    validateAndEmit(file) {
      // Check accept types if specified
      if (this.accept && this.accept !== '*/*') {
        const acceptTypes = this.accept.split(',').map(t => t.trim())
        const fileType = file.type
        const fileExt = '.' + file.name.split('.').pop().toLowerCase()

        const isValid = acceptTypes.some(type => {
          if (type.startsWith('.')) {
            return fileExt === type.toLowerCase()
          }
          if (type.endsWith('/*')) {
            return fileType.startsWith(type.replace('/*', '/'))
          }
          return fileType === type
        })

        if (!isValid) {
          this.$emit('invalid-file', file)
          return
        }
      }

      this.$emit('update:modelValue', file)
    },
    removeFile() {
      this.$refs.fileInput.value = ''
      this.$emit('update:modelValue', null)
    }
  },
  emits: ['update:modelValue', 'invalid-file']
}
</script>

<style scoped>
.file-upload-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.text-input-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.required-indicator {
  color: #dc2626;
  margin-left: 2px;
}

.file-input-hidden {
  display: none;
}

.file-upload-area {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 45px;
  min-width: 0;
  padding: 0 14px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  box-sizing: border-box;
}

.file-upload-area:hover:not(.file-upload-area--disabled) {
  border-color: #9ca3af;
}

.file-upload-area--drag-over:not(.file-upload-area--disabled) {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.file-upload-area--disabled {
  background: #fafafa;
  cursor: not-allowed;
  opacity: 0.6;
}

.file-upload-area--error {
  border-color: #dc2626;
}

/* Empty state */
.upload-icon {
  color: #9ca3af;
  flex-shrink: 0;
}

.placeholder-text {
  font-size: 1rem;
  color: #9ca3af;
}

/* File selected state */
.file-icon {
  color: #374151;
  flex-shrink: 0;
}

.file-name {
  flex: 1;
  font-size: 1rem;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.remove-btn:hover {
  background: #fee2e2;
  color: #dc2626;
}

.file-upload-error {
  font-size: 0.8125rem;
  color: #dc2626;
}
</style>
